import React, { useState, useEffect } from 'react';
import { X, Search, Image as ImageIcon } from 'lucide-react';

interface ImageItem {
  id: string;
  name: string;
  url: string;
  thumbnailUrl: string;
  size: number;
  createdAt: string;
}

interface CustomShopImageBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: (imageUrl: string) => void;
  isDarkMode?: boolean;
}

interface ApiResponse {
  success: boolean;
  images?: ImageItem[];
  error?: string;
}

const CustomShopImageBrowserModal: React.FC<CustomShopImageBrowserModalProps> = ({ 
  isOpen, 
  onClose, 
  onSelectImage, 
  isDarkMode = false 
}) => {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const filteredImages = images.filter(image =>
    image.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/list-custom-shop-icons');
      const data = await response.json() as ApiResponse;
      
      if (data.success) {
        setImages(data.images || []);
      } else {
        setError(data.error || 'Failed to load custom shop icons');
      }
    } catch (err) {
      setError('Failed to load custom shop icons');
      console.error('Error fetching custom shop icons:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchImages();
    }
  }, [isOpen]);

  const handleSelectImage = (imageUrl: string) => {
    onSelectImage(imageUrl);
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Browse Custom Shop Icons
          </h2>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className={`hover:text-gray-600 ${
              isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400'
            }`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Search */}
        <div className={`p-6 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-400'
            }`} size={20} />
            <input
              type="text"
              placeholder="Search custom shop icons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode 
                  ? 'border-gray-600 bg-gray-700 text-white' 
                  : 'border-gray-300 bg-white text-gray-900'
              }`}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className={`ml-2 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Loading custom shop icons...
              </span>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <ImageIcon className={`mx-auto h-12 w-12 ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`} />
                <p className={`mt-2 ${
                  isDarkMode ? 'text-red-400' : 'text-red-600'
                }`}>
                  {error}
                </p>
                <button
                  onClick={fetchImages}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && filteredImages.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <ImageIcon className={`mx-auto h-12 w-12 ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`} />
                <p className={`mt-2 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {searchTerm ? 'No custom shop icons found matching your search' : 'No custom shop icons found'}
                </p>
              </div>
            </div>
          )}

          {!loading && !error && filteredImages.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage === image.url
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : isDarkMode
                      ? 'border-gray-600 hover:border-blue-300'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => setSelectedImage(image.url)}
                  onDoubleClick={() => handleSelectImage(image.url)}
                >
                  <div className="aspect-square">
                    <img
                      src={image.thumbnailUrl || `${image.url}?tr=w-200,h-200,c-maintain_ratio,f-auto,q-80`}
                      alt={image.name}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        // Try the original URL with thumbnail transformation
                        const originalUrl = `${image.url}?tr=w-200,h-200,c-maintain_ratio,f-auto,q-80`;
                        if (e.currentTarget.src !== originalUrl) {
                          e.currentTarget.src = originalUrl;
                        } else {
                          // If that fails too, try without crossorigin
                          e.currentTarget.removeAttribute('crossorigin');
                          e.currentTarget.src = image.url;
                        }
                      }}
                    />
                  </div>
                  
                  {/* Overlay with info */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-end">
                    <div className="p-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-xs font-medium truncate">{image.name}</p>
                      <p className="text-xs text-gray-300">{formatFileSize(image.size)}</p>
                      <p className="text-xs text-gray-300">{formatDate(image.createdAt)}</p>
                    </div>
                  </div>

                  {/* Selection indicator */}
                  {selectedImage === image.url && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between p-6 border-t ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className={`text-sm ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {filteredImages.length > 0 && (
              <>
                {filteredImages.length} icon{filteredImages.length !== 1 ? 's' : ''} found
                {searchTerm && ` for "${searchTerm}"`}
              </>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className={`px-4 py-2 ${
                isDarkMode 
                  ? 'text-gray-400 hover:text-gray-200' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={() => selectedImage && handleSelectImage(selectedImage)}
              disabled={!selectedImage}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Select Icon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomShopImageBrowserModal;